import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MoreHorizontal } from 'lucide-react';

import { Button } from 'components/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/Dropdown';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  isCancelSupported,
  isRescheduleSupported,
} from 'constants/AppointmentSupport';
import { type Appointment } from 'lib/api/getAppointments';
import { AppointmentSources, EmailTypes } from 'types/Appointment';
import { titleCase } from 'utils/formatStrings';

dayjs.extend(utc);

type AppointmentListCardProps = {
  appointment: Appointment;
};

const formatTimezoneAgnosticDate = (dateString: string) => {
  const parsedDate = dayjs.utc(dateString);
  if (!parsedDate.isValid()) {
    return dateString;
  }

  return parsedDate.format('MM/DD/YYYY');
};

const formatTimezoneAgnosticTime = (timeString: string) => {
  const parsedTime = dayjs.utc(timeString);
  if (!parsedTime.isValid()) {
    return timeString;
  }

  return parsedTime.format('HH:mm');
};

const formatLocalDate = (dateString: string) => {
  const parsedDate = dayjs(dateString);
  if (!parsedDate.isValid()) {
    return dateString;
  }

  return parsedDate.format('MM/DD/YYYY');
};

export const AppointmentListCard = ({
  appointment,
}: AppointmentListCardProps) => {
  const canCancel = isCancelSupported(appointment.Source, appointment.tenant);
  const canReschedule = isRescheduleSupported(
    appointment.Source,
    appointment.tenant
  );

  const isEmailAppointment = appointment.Source === AppointmentSources.Email;

  const getEmailApptDescription = () => {
    const date = formatLocalDate(appointment.CreatedAt);

    if (appointment.EmailType === EmailTypes.New) {
      return (
        <>
          <Typography variant='body-xs' weight='medium' as='span'>
            Sent new
          </Typography>{' '}
          email request on {date}
        </>
      );
    } else if (appointment.EmailType === EmailTypes.Reply) {
      return (
        <>
          <Typography variant='body-xs' weight='medium' as='span'>
            Replied
          </Typography>{' '}
          in email thread on {date}
        </>
      );
    } else if (appointment.EmailType === EmailTypes.Forward) {
      return (
        <>
          <Typography variant='body-xs' weight='medium' as='span'>
            Forwarded
          </Typography>{' '}
          email on {date}
        </>
      );
    } else if (appointment.EmailType === EmailTypes.Copy) {
      return (
        <>
          <Typography variant='body-xs' weight='medium' as='span'>
            Copied
          </Typography>{' '}
          email draft from Drumkit on {date}
        </>
      );
    }

    return null;
  };

  return (
    <div className='border border-neutral-300 rounded-sm py-2 px-3 bg-neutral-50'>
      <Flex justify='between' align='start' gap='xs'>
        <div className='flex-1'>
          <Flex align='center' gap='sm' justify='between'>
            <Typography variant='body-sm' weight='semibold'>
              {titleCase(appointment.Source)}
            </Typography>

            {/* Stop type badge */}
            <span
              className={`px-1.5 py-0.5 rounded ${
                appointment.stopType.toLowerCase() === 'pickup'
                  ? 'bg-brand-50 border border-brand'
                  : 'bg-success-50 border border-success'
              }`}
            >
              <Typography
                variant='body-xxs'
                weight='medium'
                className={
                  appointment.stopType.toLowerCase() === 'pickup'
                    ? 'text-brand'
                    : 'text-success'
                }
              >
                {titleCase(appointment.stopType)}
              </Typography>
            </span>
          </Flex>

          <Flex direction='col' gap='none'>
            {isEmailAppointment && (
              <Typography variant='body-xs' textColor='muted' className='mt-1'>
                {getEmailApptDescription()}
              </Typography>
            )}

            {/* Until appts made from email request have better data parsing,
                render date/time only for portal scheduling sources, not email */}
            {!isEmailAppointment && (
              <>
                <Typography
                  variant='body-xs'
                  weight='medium'
                  textColor='muted'
                  className='flex items-center gap-1'
                >
                  Date:
                  <Typography variant='body-xs' weight='normal'>
                    {formatTimezoneAgnosticDate(appointment.Date)}
                  </Typography>
                </Typography>
                {appointment.StartTime && (
                  <Typography
                    variant='body-xs'
                    weight='medium'
                    textColor='muted'
                    className='flex items-center gap-1'
                  >
                    Time:
                    <Typography variant='body-xs' weight='normal'>
                      {formatTimezoneAgnosticTime(appointment.StartTime)}
                    </Typography>
                  </Typography>
                )}
              </>
            )}
            {(appointment.ConfirmationNo || appointment.ExternalID) && (
              <Typography
                variant='body-xs'
                weight='medium'
                textColor='muted'
                className='flex items-center gap-1'
              >
                Confirmation #:{' '}
                <Typography variant='body-xs' weight='normal'>
                  {appointment.ConfirmationNo || appointment.ExternalID}
                </Typography>
              </Typography>
            )}
            {appointment.tenant && (
              <Typography
                variant='body-xs'
                weight='medium'
                textColor='muted'
                className='flex items-center gap-1'
              >
                Tenant:
                <Typography variant='body-xs' weight='normal'>
                  {appointment.tenant}
                </Typography>
              </Typography>
            )}
          </Flex>
        </div>

        {(canCancel || canReschedule) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='sm'
                className='h-6 w-6 p-0'
                buttonNamePosthog='appointment-actions-menu'
              >
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem disabled={!canReschedule}>
                {!canReschedule ? 'Reschedule not yet supported' : 'Reschedule'}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canCancel}>
                {!canCancel ? 'Cancel not yet supported' : 'Cancel'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </Flex>
    </div>
  );
};
